import React, { RefObject, useEffect, useReducer } from 'react'
import { TooltipDictionary } from '../useMapTooltipContent'
import getRelativeCoordinates, {
  CustomOffsets
} from '../utils/getRelativeCoordinates'
import {
  ActionType,
  initialState,
  reducer,
  RegionHoverPosition
} from './reducer'

export type UseMapHoverProps = {
  svgRef: RefObject<SVGSVGElement>
  containerRef: RefObject<HTMLDivElement>
  tooltips: TooltipDictionary
  enabled?: boolean
  elementSelector: string // e.g., '[id^="regiao-"]' or '[id^="BR-"]'
  labelSelector: string // e.g., 'text' or specific pattern
  idFormatter: (id: string) => string
  getLabelGroupSelector: (formattedId: string) => string
  customOffsets?: CustomOffsets
}

function setOutlineFilter(group: SVGGElement) {
  group.setAttribute('filter', 'url(#outline-filter)')
}

function removeOutlineFilter(group: SVGGElement) {
  if (group.getAttribute('filter') === 'url(#outline-filter)') {
    group.removeAttribute('filter')
  }
}

const useMapHover = ({
  svgRef,
  containerRef,
  tooltips,
  enabled = false,
  elementSelector,
  labelSelector,
  idFormatter,
  getLabelGroupSelector,
  customOffsets = {}
}: UseMapHoverProps) => {
  const [{ regionHoverPosition, regionKey }, dispatch] = useReducer(
    reducer,
    initialState
  )

  const storedHoverPositions = React.useRef<{
    [x: string]: RegionHoverPosition
  }>({})

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !enabled) return

    const containerSize = { width: 0, height: 0 }

    const containerObserver = new ResizeObserver(([chartDiv]) => {
      const { height = 0, width = 0 } = chartDiv.contentRect || {}

      containerSize.height = height
      containerSize.width = width
    })

    if (containerRef.current) {
      containerObserver.observe(containerRef.current)
    }

    // Select all elements by the provided selector pattern
    const elementGroups =
      svgRef.current.querySelectorAll<SVGGElement>(elementSelector)

    // Initialize stroke styles on mount
    // Preserve stroke color from SVG attributes (set by applyColors) to show state boundaries
    elementGroups.forEach((elementGroup) => {
      elementGroup.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        // Get stroke color from attribute if it exists, otherwise use fill color
        const strokeColor =
          path.getAttribute('stroke') || getComputedStyle(path).stroke || '#ccc'
        path.style.stroke = strokeColor
        path.style.strokeWidth = path.getAttribute('stroke-width') || '1px'
      })
    })

    function handleMouseEnter(this: SVGGElement) {
      const rawId = this.getAttribute('id') || ''
      const id = idFormatter(rawId)

      const tooltipEstimatedHeight = tooltips[id]?.estimatedHeight || 0

      const coordinates = getRelativeCoordinates({
        group: this,
        id,
        containerSize,
        tooltipEstimatedHeight,
        customOffsets
      })

      storedHoverPositions.current[id] = coordinates

      dispatch({ type: ActionType.SET_REGION_KEY, payload: id })

      dispatch({
        type: ActionType.SET_REGION_HOVER_POSITION,
        payload: coordinates
      })

      setOutlineFilter(this)
    }

    function handleMouseLeave(this: SVGGElement) {
      dispatch({ type: ActionType.SET_REGION_KEY, payload: null })

      dispatch({ type: ActionType.SET_REGION_HOVER_POSITION, payload: null })

      // Reset stroke style to preserve visible boundaries
      this.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        // Preserve stroke color from SVG attributes to show state boundaries
        const strokeColor =
          path.getAttribute('stroke') || getComputedStyle(path).stroke || '#ccc'
        path.style.stroke = strokeColor
        path.style.strokeWidth = path.getAttribute('stroke-width') || '1px'
      })

      removeOutlineFilter(this)
    }

    function handleMouseEnterLabel(this: SVGGElement) {
      const rawId = this.getAttribute('id') || ''
      const id = idFormatter(rawId)

      const groupSelector = getLabelGroupSelector(id)
      const elementGroup =
        svgRef.current?.querySelector<SVGGElement>(groupSelector) || null

      if (!elementGroup) return

      const tooltipEstimatedHeight = tooltips[id]?.estimatedHeight || 0

      dispatch({ type: ActionType.SET_REGION_KEY, payload: id })

      dispatch({
        type: ActionType.SET_REGION_HOVER_POSITION,
        payload:
          storedHoverPositions.current[id] ||
          getRelativeCoordinates({
            group: this,
            id,
            containerSize,
            tooltipEstimatedHeight,
            customOffsets
          })
      })

      setOutlineFilter(elementGroup)
    }

    function handleMouseLeaveLabel(this: SVGGElement) {
      const rawId = this.getAttribute('id') || ''
      const id = idFormatter(rawId)

      const groupSelector = getLabelGroupSelector(id)
      const elementGroup =
        svgRef.current?.querySelector<SVGGElement>(groupSelector) || null

      if (!elementGroup) return

      dispatch({ type: ActionType.SET_REGION_KEY, payload: null })

      dispatch({ type: ActionType.SET_REGION_HOVER_POSITION, payload: null })

      elementGroup.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        // Preserve stroke color from SVG attributes to show state boundaries
        const strokeColor =
          path.getAttribute('stroke') || getComputedStyle(path).stroke || '#ccc'
        path.style.stroke = strokeColor
        path.style.strokeWidth = path.getAttribute('stroke-width') || '1px'
      })

      removeOutlineFilter(elementGroup)
    }

    elementGroups.forEach((element) => {
      element.addEventListener('mouseenter', handleMouseEnter)
      element.addEventListener('mouseleave', handleMouseLeave)
    })

    const labels = svgRef.current.querySelectorAll<SVGGElement>(labelSelector)

    labels.forEach((label) => {
      label.addEventListener('mouseenter', handleMouseEnterLabel)
      label.addEventListener('mouseleave', handleMouseLeaveLabel)
    })

    return () => {
      elementGroups.forEach((element) => {
        element.removeEventListener('mouseenter', handleMouseEnter)
        element.removeEventListener('mouseleave', handleMouseLeave)
      })

      labels.forEach((label) => {
        label.removeEventListener('mouseenter', handleMouseEnterLabel)
        label.removeEventListener('mouseleave', handleMouseLeaveLabel)
      })

      containerObserver?.disconnect()
    }
  }, [
    svgRef,
    containerRef,
    enabled,
    tooltips,
    elementSelector,
    labelSelector,
    idFormatter,
    getLabelGroupSelector,
    customOffsets
  ])

  return {
    key: regionKey,
    tooltipCoordinates: regionHoverPosition
  }
}

export default useMapHover
